/**
 * DEMO-ONLY: Auto-Scheduling Demo Simulation Service
 *
 * Simulates execution and reset of demo scenarios without touching the database.
 * Generates fake data for UI demonstration purposes only.
 */

import { addDays, addHours, startOfDay, differenceInDays } from 'date-fns';
import {
  saveDemoState,
  clearScenarioState,
  Scenario1State,
  Scenario2State,
  Scenario3State,
  MockPatientDetails,
  MockVisitDetails,
  MockEpisodeDetails,
  getNextDemoPatient,
} from './demoStateManager';
import {
  MOCK_PATIENTS,
  CARE_PATTERNS,
  CarePattern,
  MockPatient,
} from './mockAutoSchedulingData';
import { ScenarioExecutionResult } from './autoSchedulingDemo';

// Simulated IDs for demo data
let nextPatientId = 9000;
let nextEpisodeId = 8000;
let nextVisitId = 7000;

interface MockVisit {
  id: number;
  startTime: string;
  endTime: string;
  eventStatus: string;
  visitType: {
    id: number;
    name: string;
  };
  visitResources: Array<{
    id: number;
    provider: {
      id: number;
      firstName: string;
      lastName: string;
    };
  }>;
  visitServices: Array<{
    service: {
      id: number;
      name: string;
    };
  }>;
}

/**
 * Generate mock visits for a care pattern
 */
function generateMockVisits(
  pattern: CarePattern,
  startDate: Date,
  daysCount: number,
  visitTypeId: number,
  visitTypeName: string
): MockVisit[] {
  const visits: MockVisit[] = [];

  for (let day = 0; day < daysCount; day++) {
    const dayDate = addDays(startDate, day);

    for (let visitIndex = 0; visitIndex < pattern.visitsPerDay; visitIndex++) {
      const window = pattern.timeWindows[visitIndex];
      const [startHour] = window.earliestStart.split(':').map(Number);
      const visitStart = addHours(startOfDay(dayDate), startHour);
      const visitEnd = addHours(visitStart, pattern.duration / 60);

      visits.push({
        id: nextVisitId++,
        startTime: visitStart.toISOString(),
        endTime: visitEnd.toISOString(),
        eventStatus: 'scheduled',
        visitType: {
          id: visitTypeId,
          name: visitTypeName,
        },
        visitResources: [
          {
            id: nextVisitId,
            provider: {
              id: 100 + visitIndex,
              firstName: visitIndex === 0 ? 'John' : visitIndex === 1 ? 'Sarah' : 'Mike',
              lastName: visitIndex === 0 ? 'Smith' : visitIndex === 1 ? 'Johnson' : 'Williams',
            },
          },
        ],
        visitServices: [
          {
            service: {
              id: 1,
              name: 'Vitals',
            },
          },
          {
            service: {
              id: 2,
              name: 'Medication Administration',
            },
          },
        ],
      });
    }
  }

  return visits;
}

/**
 * Simulate async operation with delay
 */
async function simulateDelay(ms: number = 800): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Simulate Scenario 1: Admission Order
 */
export async function simulateAdmissionScenario(
  mockPatient: MockPatient,
  carePattern: CarePattern,
  expectedLOS: number,
  onProgress?: (step: string) => void
): Promise<ScenarioExecutionResult> {
  try {
    onProgress?.('Simulating EHR order...');
    await simulateDelay();

    // Get next unique demo patient (with iteration and timestamp)
    const demoPatient = getNextDemoPatient(MOCK_PATIENTS);

    onProgress?.('Creating patient...');
    await simulateDelay();

    const patientId = nextPatientId++;
    const episodeId = nextEpisodeId++;

    console.log(`[SIMULATION] Created patient: ${patientId} - ${demoPatient.firstName} ${demoPatient.lastName} (${demoPatient.mrn})`);

    onProgress?.('Activating episode...');
    await simulateDelay();

    console.log(`[SIMULATION] Created episode: ${episodeId}`);

    onProgress?.('Auto-scheduling visits...');

    // Generate mock visits
    const admissionDate = new Date();
    const visits = generateMockVisits(
      carePattern,
      admissionDate,
      expectedLOS,
      1,
      'Routine In-Home'
    );

    // Simulate progressive visit creation
    for (let i = 0; i < Math.min(5, visits.length); i++) {
      await simulateDelay(300);
      onProgress?.(`Auto-scheduling visits... ${i + 1}/${visits.length}`);
    }

    const visitIds = visits.map((v) => v.id);

    console.log(`[SIMULATION] Created ${visits.length} visits`);

    onProgress?.(`✓ Complete! ${visits.length} visits scheduled`);

    // Prepare patient details with unique data
    const patientDetails: MockPatientDetails = {
      id: patientId,
      firstName: demoPatient.firstName,
      lastName: demoPatient.lastName,
      mrn: demoPatient.mrn,
      dateOfBirth: demoPatient.dateOfBirth,
      phone: demoPatient.phone,
      addressStreet: demoPatient.addressStreet,
      addressCity: demoPatient.addressCity,
      addressState: demoPatient.addressState,
      addressZip: demoPatient.addressZip,
    };

    // Prepare episode details
    const episodeDetails: MockEpisodeDetails = {
      id: episodeId,
      admissionDate: admissionDate.toISOString(),
      expectedDischargeDate: addDays(admissionDate, expectedLOS).toISOString(),
      currentDay: 1,
      expectedLOS,
      carePattern: {
        name: carePattern.name,
        visitsPerDay: carePattern.visitsPerDay,
        duration: carePattern.duration,
      },
    };

    // Prepare visit details
    const visitDetails: MockVisitDetails[] = visits.map((v) => ({
      id: v.id,
      startTime: v.startTime,
      endTime: v.endTime,
      eventStatus: v.eventStatus,
      visitType: v.visitType,
      providers: v.visitResources.map((r) => r.provider),
      services: v.visitServices.map((s) => s.service),
    }));

    // Save demo state
    const scenario1State: Scenario1State = {
      patientId,
      episodeId,
      visitIds,
      executedAt: new Date().toISOString(),
      patientDetails,
      episodeDetails,
      visits: visitDetails,
    };
    saveDemoState('scenario1', scenario1State);

    return {
      success: true,
      patientId,
      episodeId,
      visitIds,
      patientDetails: {
        firstName: demoPatient.firstName,
        lastName: demoPatient.lastName,
        mrn: demoPatient.mrn,
      },
    };
  } catch (error: any) {
    console.error('[SIMULATION] Admission scenario failed:', error);
    onProgress?.(`✗ Error: ${error.message}`);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Simulate Scenario 2: Pattern Change Order
 * Cancels future visits, reduces to 1x daily, shortens LOS by 1.
 */
export async function simulatePatternChangeScenario(
  patientId: number,
  episodeId: number,
  admissionDateStr: string,
  expectedLOS: number,
  onProgress?: (step: string) => void
): Promise<ScenarioExecutionResult & { patientName?: string; canceledCount?: number; newCount?: number }> {
  try {
    onProgress?.('Simulating EHR order...');
    await simulateDelay();

    const newCarePattern = CARE_PATTERNS.singleDaily;
    // Remaining days: LOS - 2 (shorten by 1, start from tomorrow)
    const remainingDays = expectedLOS - 2;
    // Old pattern had 2x daily for (LOS - 1) remaining days
    const canceledCount = CARE_PATTERNS.standard.visitsPerDay * (expectedLOS - 1);

    onProgress?.(`Canceling ${canceledCount} future visits...`);
    await simulateDelay();

    onProgress?.('Updating pattern of care...');
    await simulateDelay();

    onProgress?.('Scheduling new visits...');

    const admissionDate = new Date(admissionDateStr);
    const tomorrowDate = addDays(admissionDate, 1);
    const newVisits = generateMockVisits(
      newCarePattern,
      tomorrowDate,
      remainingDays,
      1,
      'Routine In-Home'
    );

    for (let i = 0; i < Math.min(5, newVisits.length); i++) {
      await simulateDelay(300);
      onProgress?.(`Scheduling new visits... ${i + 1}/${newVisits.length}`);
    }

    const canceledVisitIds = Array.from({ length: canceledCount }, () => nextVisitId++);
    const newVisitIds = newVisits.map((v) => v.id);

    onProgress?.(
      `✓ Complete! ${canceledCount} visits canceled, ${newVisits.length} new visits scheduled`
    );

    // Get patient details from scenario 1
    const existingState = (window.localStorage.getItem('demo-auto-scheduling-state')
      ? JSON.parse(window.localStorage.getItem('demo-auto-scheduling-state')!)
      : {}) as any;
    const patientDetails = existingState?.scenario1?.patientDetails;
    const patientName = patientDetails
      ? `${patientDetails.firstName} ${patientDetails.lastName}`
      : undefined;

    const episodeDetails: MockEpisodeDetails = {
      id: episodeId,
      admissionDate: admissionDate.toISOString(),
      expectedDischargeDate: addDays(admissionDate, expectedLOS - 1).toISOString(),
      currentDay: 1,
      expectedLOS: expectedLOS - 1,
      carePattern: {
        name: newCarePattern.name,
        visitsPerDay: newCarePattern.visitsPerDay,
        duration: newCarePattern.duration,
      },
    };

    const visitDetails: MockVisitDetails[] = newVisits.map((v) => ({
      id: v.id,
      startTime: v.startTime,
      endTime: v.endTime,
      eventStatus: v.eventStatus,
      visitType: v.visitType,
      providers: v.visitResources.map((r) => r.provider),
      services: v.visitServices.map((s) => s.service),
    }));

    const scenario2State: Scenario2State = {
      patientId,
      episodeId,
      originalCareRequirements: [],
      canceledVisitIds,
      newVisitIds,
      executedAt: new Date().toISOString(),
      patientDetails,
      episodeDetails,
      visits: visitDetails,
    };
    saveDemoState('scenario2', scenario2State);

    return {
      success: true,
      patientId,
      episodeId,
      visitIds: newVisitIds,
      patientName,
      canceledCount,
      newCount: newVisits.length,
    };
  } catch (error: any) {
    console.error('[SIMULATION] Pattern change scenario failed:', error);
    onProgress?.(`✗ Error: ${error.message}`);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Simulate Scenario 3: Discharge Order
 */
export async function simulateDischargeScenario(
  patientId: number,
  episodeId: number,
  dischargeDate: Date,
  onProgress?: (step: string) => void
): Promise<ScenarioExecutionResult & { patientName?: string; canceledCount?: number; dischargeDate?: string }> {
  try {
    onProgress?.('Simulating EHR discharge order...');
    await simulateDelay();

    // Get patient + episode details from scenario 1 state
    const existingState = (window.localStorage.getItem('demo-auto-scheduling-state')
      ? JSON.parse(window.localStorage.getItem('demo-auto-scheduling-state')!)
      : {}) as any;
    const patientDetails = existingState?.scenario1?.patientDetails;
    const patientName = patientDetails
      ? `${patientDetails.firstName} ${patientDetails.lastName}`
      : undefined;
    const previousEpisodeDetails = existingState?.scenario2?.episodeDetails || existingState?.scenario1?.episodeDetails;
    const remainingRoutineVisits = previousEpisodeDetails
      ? (previousEpisodeDetails.expectedLOS - previousEpisodeDetails.currentDay - 1) * (previousEpisodeDetails.carePattern?.visitsPerDay ?? 1)
      : 2;
    const canceledCount = Math.max(0, remainingRoutineVisits);

    onProgress?.(`Canceling ${canceledCount} remaining routine visits...`);
    await simulateDelay();

    onProgress?.('Scheduling discharge day visits...');

    // Generate discharge day visits (1 routine + 1 discharge)
    const routineVisit: MockVisit = {
      id: nextVisitId++,
      startTime: addHours(startOfDay(dischargeDate), 9).toISOString(),
      endTime: addHours(startOfDay(dischargeDate), 10).toISOString(),
      eventStatus: 'scheduled',
      visitType: {
        id: 1,
        name: 'Routine In-Home',
      },
      visitResources: [
        {
          id: nextVisitId,
          provider: {
            id: 101,
            firstName: 'Sarah',
            lastName: 'Johnson',
          },
        },
      ],
      visitServices: [
        {
          service: {
            id: 1,
            name: 'Vitals',
          },
        },
      ],
    };

    const dischargeVisit: MockVisit = {
      id: nextVisitId++,
      startTime: addHours(startOfDay(dischargeDate), 16).toISOString(),
      endTime: addHours(startOfDay(dischargeDate), 17.5).toISOString(),
      eventStatus: 'scheduled',
      visitType: {
        id: 2,
        name: 'Discharge In-Home',
      },
      visitResources: [
        {
          id: nextVisitId,
          provider: {
            id: 102,
            firstName: 'Mike',
            lastName: 'Williams',
          },
        },
      ],
      visitServices: [
        {
          service: {
            id: 1,
            name: 'Vitals',
          },
        },
        {
          service: {
            id: 3,
            name: 'Equipment Pickup',
          },
        },
      ],
    };

    await simulateDelay();

    const updatedVisitIds = [routineVisit.id, dischargeVisit.id];

    console.log(`[SIMULATION] Updated ${updatedVisitIds.length} visits for discharge`);

    onProgress?.(`✓ Complete! Discharge scheduled for ${dischargeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);


    // Update episode details for discharge
    const episodeDetails: MockEpisodeDetails = {
      id: episodeId,
      admissionDate: previousEpisodeDetails?.admissionDate || new Date().toISOString(),
      expectedDischargeDate: dischargeDate.toISOString(),
      currentDay: previousEpisodeDetails?.currentDay || 6,
      expectedLOS: previousEpisodeDetails?.expectedLOS || 6,
      carePattern: {
        name: 'Discharge Day Pattern',
        visitsPerDay: 2,
        duration: 75, // Mix of routine (60) and discharge (90)
      },
    };

    // Prepare visit details
    const visitDetails: MockVisitDetails[] = [
      {
        id: routineVisit.id,
        startTime: routineVisit.startTime,
        endTime: routineVisit.endTime,
        eventStatus: routineVisit.eventStatus,
        visitType: routineVisit.visitType,
        providers: routineVisit.visitResources.map((r) => r.provider),
        services: routineVisit.visitServices.map((s) => s.service),
      },
      {
        id: dischargeVisit.id,
        startTime: dischargeVisit.startTime,
        endTime: dischargeVisit.endTime,
        eventStatus: dischargeVisit.eventStatus,
        visitType: dischargeVisit.visitType,
        providers: dischargeVisit.visitResources.map((r) => r.provider),
        services: dischargeVisit.visitServices.map((s) => s.service),
      },
    ];

    // Save demo state
    const scenario3State: Scenario3State = {
      patientId,
      episodeId,
      originalCareRequirements: [], // Simulated - would store original
      updatedVisitIds,
      executedAt: new Date().toISOString(),
      patientDetails,
      episodeDetails,
      visits: visitDetails,
    };
    saveDemoState('scenario3', scenario3State);

    return {
      success: true,
      patientId,
      episodeId,
      visitIds: updatedVisitIds,
      patientName,
      canceledCount,
      dischargeDate: dischargeDate.toISOString(),
    };
  } catch (error: any) {
    console.error('[SIMULATION] Discharge scenario failed:', error);
    onProgress?.(`✗ Error: ${error.message}`);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Simulate Reset Scenario 1
 */
export async function simulateResetAdmissionScenario(
  patientId: number,
  episodeId: number,
  visitIds: number[],
  onProgress?: (step: string) => void
): Promise<void> {
  onProgress?.('Resetting scenario...');
  await simulateDelay();

  onProgress?.('Canceling visits...');
  await simulateDelay();

  console.log(`[SIMULATION] Canceled ${visitIds.length} visits`);

  onProgress?.('Deactivating episode...');
  await simulateDelay();

  console.log(`[SIMULATION] Deactivated episode ${episodeId}`);

  onProgress?.('Clearing demo state...');
  await simulateDelay();

  clearScenarioState('scenario1');

  onProgress?.('✓ Reset complete');
}

/**
 * Simulate Reset Scenario 2
 */
export async function simulateResetPatternChangeScenario(
  patientId: number,
  episodeId: number,
  onProgress?: (step: string) => void
): Promise<void> {
  onProgress?.('Resetting scenario...');
  await simulateDelay();

  onProgress?.('Restoring original pattern...');
  await simulateDelay();

  console.log(`[SIMULATION] Restored original pattern for episode ${episodeId}`);

  onProgress?.('Clearing demo state...');
  await simulateDelay();

  clearScenarioState('scenario2');

  onProgress?.('✓ Reset complete');
}

/**
 * Simulate Reset Scenario 3
 */
export async function simulateResetDischargeScenario(
  patientId: number,
  episodeId: number,
  onProgress?: (step: string) => void
): Promise<void> {
  onProgress?.('Resetting scenario...');
  await simulateDelay();

  onProgress?.('Reverting discharge changes...');
  await simulateDelay();

  console.log(`[SIMULATION] Reverted discharge changes for episode ${episodeId}`);

  onProgress?.('Clearing demo state...');
  await simulateDelay();

  clearScenarioState('scenario3');

  onProgress?.('✓ Reset complete');
}

/**
 * Simulate Reset All Scenarios
 */
export async function simulateResetAllScenarios(
  onProgress?: (step: string) => void
): Promise<void> {
  onProgress?.('Resetting all scenarios...');
  await simulateDelay();

  onProgress?.('Clearing scenario 1...');
  await simulateDelay();
  clearScenarioState('scenario1');

  onProgress?.('Clearing scenario 2...');
  await simulateDelay();
  clearScenarioState('scenario2');

  onProgress?.('Clearing scenario 3...');
  await simulateDelay();
  clearScenarioState('scenario3');

  console.log('[SIMULATION] All scenarios reset');

  onProgress?.('✓ All scenarios reset successfully');
}
